import { ContentLoader, DatePicker } from "@rio-cloud/rio-uikit";
import moment, { Moment } from "moment";
import { ErrorsType } from "../index";
import { Custom } from "./Custom";
import { formatPhoneNumber } from "../../../../../utils/phoneUtils";
import { useIntl } from "react-intl";
import { VehicleSupport } from "../../../types";
import { formatEntryDate, toEntryDateValue } from "../../../entryDate";
interface Props {
    responsible: string;
    handleResponsible: (value: string) => void;
    dn: string;
    handleDn: (value: string) => void;
    chassis: string;
    handleChassis: (value: string) => void;
    licensePlate: string;
    handleLicensePlate: (value: string) => void;
    customer: string;
    handleCustomer: (value: string) => void;
    dnResponsible: string;
    handleDnResponsible: (value: string) => void;
    hourMeter: number;
    handleHourMeter: (value: number) => void;
    odometer: number;
    handleOdometer: (value: number) => void;
    entryDate: string;
    handleEntryDate: (value: string) => void;
    isTechnicalSupport: boolean;
    isLoading: boolean;
    errors: ErrorsType;
    optionsRegionals: any;
    selectedRegional?: string[];
    handleChangeRegional: (id?: string) => void;
    phone: string;
    handlePhone: (phone: string) => void;
    subject?: string;
    occurrenceId?: string | null;
    vehicle?: VehicleSupport | null;
}

type InputType = {
    label: string;
    value?: string;
    onChange?: (value: string) => void;
    isLoading: boolean;
    error?: boolean;
    placeholder?: string;
    minLength?: number;
    maxLenght?: number;
    disabled?: boolean;
};

const Input = ({
    label,
    value,
    onChange,
    isLoading,
    error,
    placeholder,
    minLength,
    maxLenght,
    disabled,
}: InputType) => {
    const intl = useIntl();

    return (
        <div className="form-group form-group-lg width-100pct">
            <label htmlFor={label}>{label}</label>
            {isLoading ? (
                <div style={{ height: 45, borderRadius: 8, overflow: "hidden" }}>
                    <ContentLoader className="width-100pct height-100pct" />
                </div>
            ) : (
                <>
                    <input
                        id={label}
                        className="form-control"
                        type="text"
                        minLength={minLength}
                        maxLength={maxLenght}
                        placeholder={placeholder}
                        value={value}
                        onChange={(evt) => onChange?.(evt.target.value)}
                        disabled={disabled}
                    />
                    <div className="height-15">
                        {error ? (
                            <span className="text-size-12 text-color-danger">
                                {minLength && value && value?.length < minLength ? intl.formatMessage({ id: "intl-msg:support.occurrence.fieldInvalid" }) : intl.formatMessage({ id: "intl-msg:support.custom.fieldRequired" })}
                            </span>
                        ) : null}
                    </div>
                </>
            )}
        </div>
    );
};

const Occurrence = ({
    responsible,
    handleResponsible,
    dn,
    handleDn,
    chassis,
    handleChassis,
    licensePlate,
    handleLicensePlate,
    customer,
    handleCustomer,
    dnResponsible,
    handleDnResponsible,
    hourMeter,
    handleHourMeter,
    odometer,
    handleOdometer,
    entryDate,
    handleEntryDate,
    isTechnicalSupport,
    isLoading,
    errors,
    optionsRegionals,
    selectedRegional,
    handleChangeRegional,
    handlePhone,
    phone,
    subject,
    occurrenceId,
    vehicle,
}: Props) => {
    const intl = useIntl();
    const requiredLabel = (id: string, required = true) => `${intl.formatMessage({ id })}${required ? '*' : ''}`;
    const isRequiredForType = subject === "TECHNICAL";
    const isRequiredForTas = isRequiredForType || isTechnicalSupport;

    return (
        <div className="width-100pct height-100pct border rounded padding-15">
            <div className="width-100pct" style={{ display: "flex", gap: 20 }}>
                <Input
                    label={`${intl.formatMessage({ id: "intl-msg:support.occurrence.responsible" })} *`}
                    value={responsible}
                    onChange={handleResponsible}
                    isLoading={isLoading}
                    // error={errors.responsible}
                    placeholder={intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                    disabled
                />
                <Input
                    label={`${intl.formatMessage({ id: "intl-msg:support.occurrence.dn" })} *`}
                    value={dn}
                    onChange={handleDn}
                    isLoading={isLoading}
                    // error={errors.dn}
                    placeholder={intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                    disabled
                />
                <Input
                    label={requiredLabel("intl-msg:support.occurrence.vehicleChassis", isRequiredForTas)}
                    value={chassis}
                    onChange={handleChassis}
                    isLoading={isLoading}
                    error={errors.chassis}
                    placeholder={isRequiredForTas ? intl.formatMessage({ id: "intl-msg:support.occurrence.required" }) : intl.formatMessage({ id: "intl-msg:support.occurrence.optional" })}
                    minLength={17}
                    maxLenght={17}
                    disabled={!!occurrenceId}
                />
                <Input
                    label={requiredLabel("intl-msg:support.occurrence.vehicleLicensePlate", isRequiredForTas)}
                    value={licensePlate}
                    onChange={handleLicensePlate}
                    isLoading={isLoading}
                    error={errors.licensePlate}
                    placeholder={isRequiredForTas ? intl.formatMessage({ id: "intl-msg:support.occurrence.required" }) : intl.formatMessage({ id: "intl-msg:support.occurrence.optional" })}
                   disabled={!!occurrenceId}
                />
            </div>
            <div className="width-100pct" style={{ display: "flex", gap: 20 }}>
                <Input
                    label={`${intl.formatMessage({ id: "intl-msg:support.occurrence.dnResponsible" })} *`}
                    value={dnResponsible}
                    onChange={handleDnResponsible}
                    isLoading={isLoading}
                    // error={errors.dnResponsible}
                    placeholder={intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                    disabled
                />
                <Input
                    label={`${intl.formatMessage({ id: "intl-msg:support.occurrence.phone" })} *`}
                    value={phone}
                    isLoading={isLoading}
                    // error={errors?.phone}
                    onChange={(e) => handlePhone(formatPhoneNumber(e))}
                    placeholder={intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                    disabled
                />
                <Input
                    label={isRequiredForType ? `${intl.formatMessage({ id: "intl-msg:support.occurrence.customer" })} *` : intl.formatMessage({ id: "intl-msg:support.occurrence.customer" })}
                    value={customer || vehicle?.vehicleOwner}
                    onChange={handleCustomer}
                    isLoading={isLoading}
                    // error={errors.customer}
                    placeholder={isRequiredForType ? intl.formatMessage({ id: "intl-msg:support.occurrence.required" }) : intl.formatMessage({ id: "intl-msg:support.occurrence.optional" })}
                    disabled
                />
                <Custom.Select
                    label={`${intl.formatMessage({ id: "intl-msg:support.occurrence.regional" })} *`}
                    options={optionsRegionals}
                    value={selectedRegional}
                    onChange={handleChangeRegional}
                    // error={errors.selectedRegional}
                    placeholder={intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                    isLoading={isLoading}
                    disabled
                />
            </div>
            <div className="width-100pct" style={{ display: "flex", gap: 20 }}>
                <Input
                    label={isRequiredForType ? `${intl.formatMessage({ id: "intl-msg:support.occurrence.hourMeter" })} *` : intl.formatMessage({ id: "intl-msg:support.occurrence.hourMeter" })}
                    value={hourMeter?.toString() || ""}
                    error={errors?.hourMeter}
                    placeholder={isRequiredForType ? intl.formatMessage({ id: "intl-msg:support.occurrence.required" }) : intl.formatMessage({ id: "intl-msg:support.occurrence.optional" })}
                    isLoading={isLoading}
                    // disabled
                    onChange={(v) => {
                        const value = Number(v);

                        if (!Number.isNaN(value)) {
                            handleHourMeter(value);
                        }
                    }}
                />

                <Input
                    label={requiredLabel("intl-msg:support.occurrence.km", isRequiredForTas)}
                    value={odometer?.toString() || ""}
                    error={errors?.odometer}
                    placeholder={isRequiredForTas ? intl.formatMessage({ id: "intl-msg:support.occurrence.required" }) : intl.formatMessage({ id: "intl-msg:support.occurrence.optional" })}
                    isLoading={isLoading}
                    // disabled
                    onChange={(v) => {
                        const value = Number(v);

                        if (!Number.isNaN(value)) {
                            handleOdometer(value);
                        }
                    }}
                />

                <div className="form-group form-group-lg width-100pct">
                    <label htmlFor={"date"}>{requiredLabel("intl-msg:tas.occurrence.entryDateTime", isTechnicalSupport || isRequiredForType)}</label>
                    {isLoading ? (
                        <div style={{ borderRadius: 8, height: 45, overflow: "hidden" }}>
                            <ContentLoader className="width-100pct height-100pct" />
                        </div>
                    ) : (
                        <>
                            <DatePicker
                                id="2"
                                inputProps={{ placeholder: intl.formatMessage({ id: "intl-msg:tas.support.toolbar.selectPlaceholder" }) }}
                                className="margin-bottom-0"
                                dateFormat="DD/MM/YYYY"
                                value={formatEntryDate(entryDate)}
                                timeFormat="HH:mm"
                                onChange={(result: Moment | string) =>
                                    handleEntryDate(toEntryDateValue(result))
                                }
                                isValidDate={(currentDate: Moment) =>
                                    currentDate.isSameOrBefore(moment(), "day")
                                }
                            />
                            <div className="height-15">
                                {errors.entryDate ? (
                                    <span className="text-size-12 text-color-danger">
                                        {intl.formatMessage({ id: "intl-msg:support.occurrence.required" })}
                                    </span>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Occurrence;
